import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BookOpen, Calculator, Languages, Trophy, Brain, Users, Clock, Target,
  ChevronRight, Star, Zap, Award, TrendingUp, Sparkles
} from 'lucide-react';

const features = [
  {
    icon: <Calculator className="w-6 h-6" />,
    title: '数学题库',
    description: '代数、几何、统计学等Year 7-10全部知识点',
    color: 'from-blue-500 to-cyan-500',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-600',
  },
  {
    icon: <Languages className="w-6 h-6" />,
    title: '英语题库',
    description: '阅读理解、写作、语法、词汇全覆盖',
    color: 'from-emerald-500 to-teal-500',
    bgColor: 'bg-emerald-50',
    textColor: 'text-emerald-600',
  },
  {
    icon: <Target className="w-6 h-6" />,
    title: 'NAPLAN模拟',
    description: '针对NAPLAN考试的专项训练与模拟测试',
    color: 'from-purple-500 to-indigo-500',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-600',
  },
  {
    icon: <TrendingUp className="w-6 h-6" />,
    title: 'VCE预备',
    description: '提前适应VCE难度，打好高中学习基础',
    color: 'from-orange-500 to-amber-500',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-600',
  },
  {
    icon: <Brain className="w-6 h-6" />,
    title: '错题分析',
    description: 'AI智能分析薄弱知识点，个性化推荐练习',
    color: 'from-pink-500 to-rose-500',
    bgColor: 'bg-pink-50',
    textColor: 'text-pink-600',
  },
  {
    icon: <Award className="w-6 h-6" />,
    title: '成就系统',
    description: '勋章墙、排行榜，让学习充满动力',
    color: 'from-amber-500 to-yellow-500',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-600',
  },
];

const grades = [
  { year: 7, label: 'Year 7', description: '初中一年级', color: 'from-cyan-500 to-blue-500' },
  { year: 8, label: 'Year 8', description: '初中二年级', color: 'from-blue-500 to-indigo-500' },
  { year: 9, label: 'Year 9', description: '初中三年级', color: 'from-indigo-500 to-purple-500' },
  { year: 10, label: 'Year 10', description: '初中四年级', color: 'from-purple-500 to-pink-500' },
];

const stats = [
  { value: '5000+', label: '精选习题' },
  { value: '32', label: '知识点' },
  { value: '10+', label: '成就徽章' },
  { value: '实时', label: '进度追踪' },
];

export function HomePage() {
  return (
    <div className="relative overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center">
        {/* Background decorations */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-400/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-400/10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur rounded-full text-sm text-blue-600 font-medium mb-6 shadow-lg shadow-blue-500/10">
                <Sparkles className="w-4 h-4" />
                专为澳洲中学生设计
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                智能学习
                <span className="block bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  轻松提分
                </span>
              </h1>

              <p className="text-lg text-slate-600 mb-8 max-w-lg leading-relaxed">
                覆盖Year 7-10全部课程，智能题库、错题分析、NAPLAN模拟、VCE预备，
                让每一次练习都更有效率。
              </p>

              <div className="flex flex-wrap gap-4">
                <Link
                  to="/login"
                  className="group inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-2xl hover:shadow-xl hover:shadow-blue-500/30 transition-all hover:scale-105"
                >
                  免费开始学习
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/subjects"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-white text-slate-700 font-semibold rounded-2xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-all"
                >
                  <BookOpen className="w-5 h-5" />
                  浏览题库
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-4 mt-12 pt-8 border-t border-slate-200">
                {stats.map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="text-center"
                  >
                    <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
                    <div className="text-sm text-slate-500">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right Content - Interactive Cards */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative w-full aspect-square">
                {/* Floating cards */}
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="absolute top-8 left-4 w-48 bg-white rounded-2xl p-4 shadow-xl shadow-blue-500/10"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Calculator className="w-5 h-5 text-blue-600" />
                    </div>
                    <span className="font-semibold text-slate-800">数学</span>
                  </div>
                  <div className="text-sm text-slate-500">16个知识点</div>
                  <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full w-3/4 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full" />
                  </div>
                </motion.div>

                <motion.div
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 4, repeat: Infinity }}
                  className="absolute top-20 right-8 w-48 bg-white rounded-2xl p-4 shadow-xl shadow-emerald-500/10"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <Languages className="w-5 h-5 text-emerald-600" />
                    </div>
                    <span className="font-semibold text-slate-800">英语</span>
                  </div>
                  <div className="text-sm text-slate-500">16个知识点</div>
                  <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full w-2/3 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full" />
                  </div>
                </motion.div>

                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 3.5, repeat: Infinity }}
                  className="absolute bottom-32 left-8 w-52 bg-white rounded-2xl p-4 shadow-xl shadow-amber-500/10"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="w-5 h-5 text-amber-500" />
                    <span className="font-semibold text-slate-800">今日成就</span>
                  </div>
                  <div className="flex gap-2 mt-3">
                    {['🏅', '⭐', '🔥'].map((emoji, i) => (
                      <div key={i} className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center text-lg">
                        {emoji}
                      </div>
                    ))}
                  </div>
                </motion.div>

                <motion.div
                  animate={{ y: [0, 12, 0] }}
                  transition={{ duration: 4.5, repeat: Infinity }}
                  className="absolute bottom-16 right-4 w-56 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl p-4 shadow-xl shadow-purple-500/20 text-white"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-5 h-5" />
                    <span className="font-semibold">学习连续</span>
                  </div>
                  <div className="text-3xl font-bold">7 天</div>
                  <div className="text-purple-200 text-sm">保持这个势头！</div>
                </motion.div>

                {/* Center illustration */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-3xl flex items-center justify-center shadow-2xl shadow-blue-500/30">
                  <BookOpen className="w-14 h-14 text-white" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              全方位学习支持
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              从题库练习到模拟考试，从错题分析到成就激励，一站式解决所有学习需求
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative bg-white rounded-2xl p-6 border border-slate-100 hover:border-slate-200 hover:shadow-xl transition-all duration-300"
              >
                <div className={`w-12 h-12 ${feature.bgColor} rounded-xl flex items-center justify-center ${feature.textColor} mb-4 group-hover:scale-110 transition-transform`}>
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
                <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${feature.color} rounded-b-2xl opacity-0 group-hover:opacity-100 transition-opacity`} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Grade Selection */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              选择你的年级
            </h2>
            <p className="text-lg text-slate-600">
              内容精准匹配澳大利亚课程标准
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {grades.map((grade, index) => (
              <motion.div
                key={grade.year}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={`/subjects?grade=${grade.year}`}
                  className="block group"
                >
                  <div className={`relative overflow-hidden bg-gradient-to-br ${grade.color} rounded-2xl p-8 text-white shadow-lg hover:shadow-xl transition-all hover:scale-105`}>
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
                    
                    <div className="relative">
                      <div className="text-5xl font-bold mb-2">{grade.year}</div>
                      <div className="text-lg font-semibold opacity-90">{grade.label}</div>
                      <div className="text-sm opacity-75 mt-1">{grade.description}</div>
                      
                      <div className="flex items-center gap-1 mt-4 text-sm font-medium opacity-75 group-hover:opacity-100 transition-opacity">
                        开始学习
                        <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-700" />
        <div className="absolute inset-0 bg-[linear-gradient(45deg,rgba(255,255,255,0.05)_25%,transparent_25%,transparent_50%,rgba(255,255,255,0.05)_50%,rgba(255,255,255,0.05)_75%,transparent_75%)] bg-[length:60px_60px] opacity-50" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur rounded-full text-white/90 text-sm font-medium mb-6">
              <Star className="w-4 h-4" />
              完全免费，立即开始
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              准备好提升成绩了吗？
            </h2>
            
            <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
              加入数千名澳洲学生，开启高效学习之旅。无论是日常练习还是备考NAPLAN，我们都能帮助你。
            </p>
            
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-blue-600 font-semibold rounded-2xl hover:shadow-xl hover:shadow-black/20 transition-all hover:scale-105"
            >
              立即注册，免费使用
              <ChevronRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
